import { useState, useEffect, useRef } from 'react';

// const PING_INTERVAL_MS = 10000; // Ping every 10 seconds
// const SLOW_CONNECTION_THRESHOLD_MS = 1500; // If a simple ping takes > 1.5s, it's slow
// const TIMEOUT_MS = 5000; // If a simple ping takes > 5s, consider it offline

const PING_INTERVAL_MS = 10000;
const SLOW_CONNECTION_THRESHOLD_MBPS = 1; // under 1 Mbps = slow
const TIMEOUT_MS = 8000;

// ~100KB file from a reliable CDN — small enough to be fast, large enough to measure
const TEST_FILE_URL = 'https://ajax.googleapis.com/ajax/libs/jquery/3.7.1/jquery.min.js';
const TEST_FILE_SIZE_BYTES = 94000; // approximate

const useNetworkStatus = ({ isSuspended = false } = {}) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSlow, setIsSlow] = useState(false);
  const [didReconnect, setDidReconnect] = useState(false); // New flag to show "Back Online" banner
  const [networkSpeed, setNetworkSpeed] = useState(null); // Track ping or Mbps
  const intervalRef = useRef(null);
  const prevStatus = useRef({ isOnline: navigator.onLine, isSlow: false });

  // Guarantee to trigger the reconnect banner when connection returns to good
  useEffect(() => {
    const wasBadConnection = !prevStatus.current.isOnline || prevStatus.current.isSlow;
    const isGoodConnection = isOnline && !isSlow;

    if (wasBadConnection && isGoodConnection) {
      setDidReconnect(true);
    }
    
    prevStatus.current = { isOnline, isSlow };
  }, [isOnline, isSlow]);

  useEffect(() => {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    const handleOnline = () => {
      setIsOnline(true);
      if (!isSuspended) {
        runNetworkCheck();
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setIsSlow(false); 
      setNetworkSpeed(null);
    };

    // 2. Main Network Check (Primary Connection API + Fallback Resource Timing Fetch)
    const runNetworkCheck = async () => {
      if (!navigator.onLine) {
        console.log('[NetworkStatus] Offline detected via navigator.onLine');
        setIsOnline(false);
        setIsSlow(false);
        setNetworkSpeed(null);
        return;
      }

      let hasNativeEstimate = false;

      // Check Native Connection API first (Primary)
      if (connection && typeof connection.downlink === 'number') {
        hasNativeEstimate = true;
        const downlink = connection.downlink;
        const speedVal = `${downlink.toFixed(1)} Mbps`;
        const isConnectionSlow = connection.effectiveType === 'slow-2g' || 
                                 connection.effectiveType === '2g' || 
                                 downlink < 1.0;
        console.log(`[NetworkStatus] Native Connection API: ${speedVal}, type: ${connection.effectiveType}, isSlow: ${isConnectionSlow}`);
        setNetworkSpeed(speedVal);
        setIsSlow(isConnectionSlow);
      }

      // Perform Fetch (Online status truth check + Resource Timing fallback)
      try {
        const uniqueUrl = `${TEST_FILE_URL}?_=${Date.now()}`;
        const startTime = performance.now();

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

        const response = await fetch(uniqueUrl, {
          method: 'GET',
          signal: controller.signal,
          cache: 'no-store',
          mode: 'cors',
        });

        // Must consume body to ensure complete download timing is captured
        const blob = await response.blob();
        clearTimeout(timeoutId);

        setIsOnline(true);

        // Fallback Connection Speed (only if browser does not support navigator.connection)
        if (!hasNativeEstimate) {
          const endTime = performance.now();
          let duration = (endTime - startTime) / 1000;
          let bytes = blob.size || TEST_FILE_SIZE_BYTES;
          let isResourceTimingUsed = false;

          // Query the actual PerformanceResourceTiming entries
          const entries = performance.getEntriesByName(uniqueUrl);
          if (entries && entries.length > 0) {
            const entry = entries[entries.length - 1];
            if (entry.responseStart && entry.responseEnd) {
              const transferTime = (entry.responseEnd - entry.responseStart) / 1000;
              const transferBytes = entry.encodedBodySize || entry.transferSize;
              
              if (transferTime > 0.005 && transferBytes > 0) {
                duration = transferTime;
                bytes = transferBytes;
                isResourceTimingUsed = true;
              }
            }
          }

          // Clear resource timings to preserve memory
          if (typeof performance.clearResourceTimings === 'function') {
            performance.clearResourceTimings();
          }

          const mbps = (bytes * 8) / duration / 1_000_000;
          const cappedMbps = mbps > 150 ? 150 : mbps;
          const speedVal = `${cappedMbps.toFixed(1)} Mbps`;
          const isSlowConnection = cappedMbps < SLOW_CONNECTION_THRESHOLD_MBPS;

          console.log(`[NetworkStatus] Fallback Speed calculated: ${speedVal} (using Resource Timing: ${isResourceTimingUsed}), isSlow: ${isSlowConnection}`);

          setNetworkSpeed(speedVal);
          setIsSlow(isSlowConnection);
        }

      } catch (error) {
        console.log('[NetworkStatus] Active connectivity check fetch failed:', error.message);
        // If the fetch fails completely, we treat it as offline (captive portal / hotspot issue)
        setIsOnline(false);
        setIsSlow(false);
        setNetworkSpeed(null);
      }
    };

    // Setup window listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    if (connection) {
      connection.addEventListener('change', runNetworkCheck);
    }

    // Only start polling and checks if we are not suspended
    if (!isSuspended) {
      runNetworkCheck(); // Initial check
      intervalRef.current = setInterval(runNetworkCheck, PING_INTERVAL_MS);
    } else {
      // Clear status when suspended
      setIsSlow(false);
      setNetworkSpeed(null);
    }

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (connection) {
        connection.removeEventListener('change', runNetworkCheck);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isSuspended]);

  const clearReconnectStatus = () => setDidReconnect(false);

  return { isOnline, isSlow, didReconnect, networkSpeed, clearReconnectStatus };
};

export default useNetworkStatus;
